import React, { useEffect } from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import RadioButtonGroup from 'lib/components/Checkbox/RadioButtonGroup';
import { Alert } from 'lib/components/Alert';
import { EOrganization, ESnapshotExists, MailDelivery } from 'lib/types';
import { ShippingTier } from 'lib/types/mail';
import { State } from 'lib/enums';
import { NoticeAddOnPricingConfig } from 'lib/pricing/noticeAddOns/types';
import { AffidavitTypeSelector } from './AffidavitTypeSelector';
import { WetSigShippingOptions } from './WetSigShippingOptions';
import { MailRecipientsManager } from './MailRecipientsManager';
import {
  ADDONS_COPY,
  affidavitSlaMessage,
  noticeTypeMailingOutcome,
  wetSigAdditionalYesOptionLabel
} from './addOnsCopy';
import {
  AffidavitType,
  allowedAffidavitTypes,
  derivePaperType,
  formatCents,
  areMailedDocsDisabledForPublisher,
  getGroundShippingFeeCents,
  PaperType,
  resolveEffectiveAffidavitType,
  ShippingSpeed,
  shippingSpeedToTier
} from './pricingConfig';
import { actions } from '../NoticeDelivery/deliveryRecipients.slice';
import {
  affidavitAddressRequired,
  affidavitMailStamp,
  noticeTypeDefaultRecipientsInvalid
} from '../NoticeDelivery/deliveryRecipients.helpers';

type DeliveryOption = 'email' | 'mail';

/** Display-only address line; missing pieces are skipped, not flagged. */
function formatMailingAddressLine(recipient: MailDelivery): string {
  const { address } = recipient;
  const stateLabel =
    State.by_value(address?.address_state)?.label ??
    address?.address_state?.toString() ??
    '';
  const parts = [
    address?.address_line1,
    address?.address_line2,
    address?.address_city,
    stateLabel
  ]
    .map(part => (part ?? '').toString().trim())
    .filter(part => part !== '');
  const zip = (address?.address_zip ?? '').trim();
  return `${parts.join(', ')}${zip ? ` ${zip}` : ''}`;
}

type AffidavitAddOnsSectionProps = {
  dispatch: React.Dispatch<AnyAction>;
  publisherOrganization: ESnapshotExists<EOrganization> | undefined;
  wetSigRequired: boolean;
  affidavitsManagedByColumn: boolean;
  /**
   * Whether the wet-signature option is offered on an AA RON paper. False leaves
   * only the digital (RON) affidavit with an email/mail choice.
   */
  wetSigOffered: boolean;
  mailingRecipients: MailDelivery[];
  /** Preset addresses mailed at the publisher's expense; read-only here. */
  noticeTypeDefaultRecipients: MailDelivery[];
  affidavitType: AffidavitType | null;
  shippingSpeed: ShippingSpeed;
  deliveryOption: DeliveryOption;
  wantsAdditionalAffidavit: boolean;
  recipientValidationErrors: string[];
  showErrors: boolean;
};

/**
 * Flag-gated affidavit add-on flow. The top-level question depends on the
 * paper type:
 *   - Wet-sig-required: "Would you like to receive more than one wet-signature
 *     affidavit?" — the first affidavit is included/free, extras are paid.
 *   - RON (AA) with the wet-sig option: "How would you like your affidavit
 *     notarized?" — digital (RON) is free; wet-signature is paid. Digital then
 *     asks how to receive it (email/mail).
 *   - Non-AA, or a RON paper that doesn't offer wet-signature: "How would you
 *     like to receive your affidavit?" — email (free) or a mailed copy (paid).
 */
export function AffidavitAddOnsSection({
  dispatch,
  publisherOrganization,
  wetSigRequired,
  affidavitsManagedByColumn,
  wetSigOffered,
  mailingRecipients,
  noticeTypeDefaultRecipients,
  affidavitType,
  shippingSpeed,
  deliveryOption,
  wantsAdditionalAffidavit,
  recipientValidationErrors,
  showErrors
}: AffidavitAddOnsSectionProps) {
  const paperType = derivePaperType({
    publisherOrganization,
    wetSigRequired,
    affidavitsManagedByColumn
  });
  const slaMessage = affidavitSlaMessage(paperType, wetSigOffered);

  const effectiveType = resolveEffectiveAffidavitType({
    publisherOrganization,
    wetSigRequired,
    affidavitsManagedByColumn,
    storedAffidavitType: affidavitType,
    wetSigOffered
  });

  const isWetSig = effectiveType === 'wet_sig';

  const config: NoticeAddOnPricingConfig =
    publisherOrganization?.data().noticeAddOnPricing ?? {};
  const mailPerCopyCents = getGroundShippingFeeCents(config);
  const hideMailingOptions = areMailedDocsDisabledForPublisher(
    publisherOrganization
  );
  const isWetSigRequired =
    paperType === PaperType.WET_SIG_REQUIRED ||
    paperType === PaperType.WET_SIG_REQUIRED_NO_MAILING;

  const offersAdditionalWetSigs = paperType === PaperType.WET_SIG_REQUIRED;
  const allowed = allowedAffidavitTypes(paperType, wetSigOffered);
  // A RON paper offering wet-signature leads with the notarization choice.
  const offersNotarizationChoice =
    allowed.includes('ron') &&
    allowed.includes('wet_sig') &&
    !hideMailingOptions;

  // Wet-sig-required papers always mail the included copy, so an address is
  // mandatory; "more than one" adds a separate address for the paid extras.
  const optedIntoMore = offersAdditionalWetSigs && wantsAdditionalAffidavit;
  const lockToSingleCopy = isWetSigRequired && !optedIntoMore;
  const firstRowIsFreeCopy = isWetSigRequired && optedIntoMore;

  // Blocks the step (see selectIsFormComplete): the filer can't fix these.
  const presetAddressesInvalid = noticeTypeDefaultRecipientsInvalid(
    noticeTypeDefaultRecipients
  );

  // Shared with completeness + submit validation so the rule can't drift.
  const needsAddress =
    isWetSigRequired || affidavitAddressRequired(effectiveType, deliveryOption);

  function stampRecipients(recipients: MailDelivery[]): MailDelivery[] {
    const { documentType, provider } = affidavitMailStamp(effectiveType);
    const tier = isWetSig
      ? shippingSpeedToTier(shippingSpeed)
      : ShippingTier.GROUND;
    return recipients.map(r => ({
      ...r,
      documentType,
      shippingTier: tier,
      provider,
      isCourthouse: r.isCourthouse ?? false
    }));
  }

  // Pre-stamped template for MailRecipientsManager's phantom/new rows and for
  // seeding the additional address on opt-in.
  const affidavitBlankRow = stampRecipients([
    {
      address: {
        address_line1: '',
        address_line2: '',
        address_city: '',
        address_state: '',
        address_zip: ''
      },
      copies: 1,
      description: '',
      name: '',
      isCourthouse: false
    } as MailDelivery
  ])[0];

  function handleAffidavitTypeChange(type: AffidavitType) {
    dispatch(actions.setAffidavitType(type));
  }

  function handleDeliveryOptionChange(option: DeliveryOption) {
    dispatch(actions.setDeliveryOption(option));
  }

  function handleShippingSpeedChange(speed: ShippingSpeed) {
    dispatch(actions.setShippingSpeed(speed));
  }

  function handleWantsAdditionalAffidavitChange(wants: boolean) {
    dispatch(actions.setWantsAdditionalAffidavit(wants));
    if (wants) {
      // The included copy stays at the first address (copies 1); add a separate
      // address to collect the paid additional affidavits (default one).
      const included = {
        ...(mailingRecipients[0] ?? affidavitBlankRow),
        copies: 1
      };
      const additional =
        mailingRecipients.length > 1
          ? mailingRecipients.slice(1)
          : [{ ...affidavitBlankRow, copies: 1 }];
      dispatch(actions.setMailingRecipients([included, ...additional]));
      return;
    }
    // On opt-out, revert to the free defaults so a lingering paid choice doesn't
    // keep demanding an address.
    if (deliveryOption === 'mail') {
      dispatch(actions.setDeliveryOption('email'));
    }
    // Clear any upgraded shipping speed
    if (shippingSpeed !== 'ground') {
      dispatch(actions.setShippingSpeed('ground'));
    }
    const defaultType = allowed[0];
    if (defaultType && defaultType !== affidavitType) {
      dispatch(actions.setAffidavitType(defaultType));
    }
    // Keep the mandatory address but collapse extras back to the single Ground
    // copy so the summary drops both the extra affidavit fee and its shipping.
    if (isWetSigRequired) {
      const first = mailingRecipients[0];
      if (
        first &&
        (mailingRecipients.length > 1 ||
          (first.copies ?? 1) !== 1 ||
          first.shippingTier !== ShippingTier.GROUND)
      ) {
        dispatch(
          actions.setMailingRecipients([
            { ...first, copies: 1, shippingTier: ShippingTier.GROUND }
          ])
        );
      }
    }
  }

  function handleMailRecipientsChange(recipients: MailDelivery[]) {
    dispatch(actions.setMailingRecipients(stampRecipients(recipients)));
  }

  // Wisconsin never offered mailing, but Lee and Hoffman did until now, so a
  // draft started earlier can still carry a 'mail' choice. Hiding the radio
  // option does not clear it: the notice would keep demanding an address and
  // still price the mailed copy.
  useEffect(() => {
    if (hideMailingOptions && deliveryOption === 'mail') {
      dispatch(actions.setDeliveryOption('email'));
    }
  }, [hideMailingOptions, deliveryOption, dispatch]);

  useEffect(() => {
    if (effectiveType !== affidavitType) {
      dispatch(actions.setAffidavitType(effectiveType));
    }

    // Clear recipients when no address is needed (e.g. RON + email); otherwise
    // re-stamp documentType/shippingTier/provider as the type or speed changes.
    if (!needsAddress) {
      if (mailingRecipients.length > 0) {
        dispatch(actions.setMailingRecipients([]));
      }
      return;
    }
    // Skip stamping the legacy-seeded placeholder — only re-stamp recipients
    // that the user has actually started filling in (non-empty address_line1).
    const filledRecipients = mailingRecipients.filter(
      r => (r.address?.address_line1 ?? '').trim() !== ''
    );
    if (filledRecipients.length === 0) {
      return;
    }
    const restamped = stampRecipients(mailingRecipients);
    const needsUpdate = mailingRecipients.some(
      (r, i) =>
        r.documentType !== restamped[i].documentType ||
        r.shippingTier !== restamped[i].shippingTier ||
        r.provider !== restamped[i].provider
    );
    if (needsUpdate) {
      dispatch(actions.setMailingRecipients(restamped));
    }
  }, [
    effectiveType,
    affidavitType,
    dispatch,
    shippingSpeed,
    needsAddress,
    mailingRecipients,
    isWetSig
  ]);

  return (
    <div className="space-y-6 mt-4">
      {slaMessage && (
        <Alert
          id="affidavit-sla-note"
          icon={<InformationCircleIcon className="w-5" />}
          description={slaMessage}
        />
      )}

      {noticeTypeDefaultRecipients.length > 0 && (
        <Alert
          id="affidavit-notice-type-mailing"
          status={presetAddressesInvalid ? 'error' : undefined}
          icon={<InformationCircleIcon className="w-5" />}
          description={
            <div className="space-y-2">
              <p>{ADDONS_COPY.noticeTypeMailingIntro}</p>
              <ul className="list-disc list-inside">
                {noticeTypeDefaultRecipients.map((recipient, index) => (
                  <li key={`${recipient.name}-${index}`}>
                    {recipient.name ? `${recipient.name} — ` : ''}
                    {formatMailingAddressLine(recipient) ||
                      ADDONS_COPY.noticeTypeMailingMissingAddress}
                  </li>
                ))}
              </ul>
              <p>
                {noticeTypeMailingOutcome({
                  invalid: presetAddressesInvalid,
                  isWetSig,
                  mailsOwnCopy: deliveryOption === 'mail'
                })}
              </p>
            </div>
          }
        />
      )}

      {/* Flow A — wet-signature-required: one included, extras are paid. */}
      {offersAdditionalWetSigs && (
        <>
          <RadioButtonGroup
            id="affidavit-opt-in"
            labelText={ADDONS_COPY.wetSigAdditionalLegend}
            options={[
              {
                value: 'no',
                labelText: ADDONS_COPY.wetSigAdditionalNoOption
              },
              {
                value: 'yes',
                labelText: wetSigAdditionalYesOptionLabel(config)
              }
            ]}
            value={optedIntoMore ? 'yes' : 'no'}
            onChange={value =>
              handleWantsAdditionalAffidavitChange(value === 'yes')
            }
            stacked
          />
          {optedIntoMore && (
            <WetSigShippingOptions
              speed={shippingSpeed}
              onSpeedChange={handleShippingSpeedChange}
              config={config}
            />
          )}
        </>
      )}

      {/* Flow B — RON (AA) paper that offers the wet-signature option. */}
      {!isWetSigRequired && offersNotarizationChoice && (
        <>
          <AffidavitTypeSelector
            paperType={paperType}
            value={effectiveType}
            onChange={handleAffidavitTypeChange}
            config={config}
          />
          {isWetSig ? (
            <WetSigShippingOptions
              speed={shippingSpeed}
              onSpeedChange={handleShippingSpeedChange}
              config={config}
            />
          ) : (
            <MailDeliveryRadio
              hideMailingOptions={hideMailingOptions}
              legend={ADDONS_COPY.deliveryLegend}
              mailPerCopyCents={mailPerCopyCents}
              deliveryOption={deliveryOption}
              handleDeliveryOptionChange={handleDeliveryOptionChange}
            />
          )}
        </>
      )}

      {/* Flow C — non-AA, or a RON paper without the wet-signature option. */}
      {!isWetSigRequired && !offersNotarizationChoice && (
        <MailDeliveryRadio
          hideMailingOptions={hideMailingOptions}
          legend={ADDONS_COPY.nonAaDeliveryLegend}
          mailPerCopyCents={mailPerCopyCents}
          deliveryOption={deliveryOption}
          handleDeliveryOptionChange={handleDeliveryOptionChange}
        />
      )}

      {needsAddress && (
        <MailRecipientsManager
          recipients={mailingRecipients}
          onChange={handleMailRecipientsChange}
          errors={showErrors ? recipientValidationErrors : undefined}
          blankRow={affidavitBlankRow}
          lockToSingleCopy={lockToSingleCopy}
          firstRowIsFreeCopy={firstRowIsFreeCopy}
          copiesLabel={
            firstRowIsFreeCopy
              ? 'Number of additional affidavits'
              : 'Number of copies'
          }
        />
      )}
    </div>
  );
}

function MailDeliveryRadio({
  legend,
  mailPerCopyCents,
  deliveryOption,
  handleDeliveryOptionChange,
  hideMailingOptions
}: {
  legend: string;
  mailPerCopyCents: number;
  deliveryOption: DeliveryOption;
  handleDeliveryOptionChange: (option: DeliveryOption) => void;
  hideMailingOptions?: boolean;
}) {
  const options = [
    {
      value: 'email' as DeliveryOption,
      labelText: ADDONS_COPY.deliveryEmailOption,
      labelDescription: ADDONS_COPY.deliveryEmailDescription
    }
  ];

  if (!hideMailingOptions) {
    options.push({
      value: 'mail' as DeliveryOption,
      labelText: `Mail a copy — ${formatCents(mailPerCopyCents)} per copy`,
      labelDescription: ADDONS_COPY.deliveryMailOptionDescription
    });
  }

  return (
    <RadioButtonGroup
      id="affidavit-delivery-option"
      labelText={legend}
      options={options}
      value={deliveryOption}
      onChange={handleDeliveryOptionChange}
      stacked
    />
  );
}
